import moment from 'moment-timezone'
import {useState, useCallback, useMemo} from 'react'
import {useForm, UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DayPeriod, StrengthTarget, StrengthTargetFormData} from '../types'
import {
  isoToLocalDateInTimezone,
  localDateToISOInTimezone,
  compareDateStrings
} from '../utils/dateUtils'
import {
  validateTargetValue,
  validateLowerBoundValue,
  validateUpperBoundValue,
  validateValidFromValue,
  hasAtLeastOneValue,
  findNextAvailableDate
} from '../utils/strengthTargetFormValidation'

interface UseStrengthTargetFormProps {
  target?: StrengthTarget
  defaultDayPeriod: DayPeriod
  timezone: string
  existingTargets: StrengthTarget[]
  backendTargets: StrengthTarget[]
  onSave: (target: StrengthTarget) => void
  onClose: () => void
}

interface UseStrengthTargetFormReturn {
  form: UseFormReturn<StrengthTargetFormData>
  isEdit: boolean
  createAnother: boolean
  showAtLeastOneValueError: boolean
  hasAnyErrors: boolean
  setCreateAnother: (value: boolean) => void
  onSubmit: (data: StrengthTargetFormData) => void
  resetFormForCreateAnother: (data: StrengthTargetFormData, newTarget: StrengthTarget) => void
}

function getDefaultValues(
  target: StrengthTarget | undefined,
  defaultDayPeriod: DayPeriod,
  timezone: string,
  existingTargets: StrengthTarget[],
  backendTargets: StrengthTarget[]
): StrengthTargetFormData {
  if (target?.validFrom) {
    return {
      dayPeriod: target.dayPeriod,
      target: target.target,
      lowerBound: target.lowerBound,
      upperBound: target.upperBound,
      validFrom: isoToLocalDateInTimezone(target.validFrom, timezone),
      comment: target.comment || '',
      createAnother: false
    }
  }

  const tomorrowISO = moment.tz(timezone).add(1, 'day').startOf('day').toISOString()

  const nextAvailableDate = findNextAvailableDate(
    defaultDayPeriod,
    existingTargets,
    backendTargets,
    tomorrowISO,
    isoToLocalDateInTimezone,
    timezone
  )

  return {
    dayPeriod: defaultDayPeriod,
    target: undefined,
    lowerBound: undefined,
    upperBound: undefined,
    validFrom: nextAvailableDate,
    comment: '',
    createAnother: false
  }
}

export function useStrengthTargetForm({
  target,
  defaultDayPeriod,
  timezone,
  existingTargets,
  backendTargets,
  onSave,
  onClose
}: UseStrengthTargetFormProps): UseStrengthTargetFormReturn {
  const {t} = useTranslation()
  const isEdit = !!target
  const [createAnother, setCreateAnother] = useState(false)

  const defaultValues = useMemo(
    () => getDefaultValues(target, defaultDayPeriod, timezone, existingTargets, backendTargets),
    [target, defaultDayPeriod, timezone, existingTargets, backendTargets]
  )

  const form = useForm<StrengthTargetFormData>({
    defaultValues,
    mode: 'onChange',
    resolver: (values) => {
      const errors: Record<string, {type: string; message: string}> = {}

      const targetError = validateTargetValue(
        values.target,
        values.lowerBound,
        values.upperBound,
        t
      )
      if (targetError !== true) {
        errors.target = {type: 'validate', message: targetError}
      }

      const lowerBoundError = validateLowerBoundValue(
        values.lowerBound,
        values.target,
        values.upperBound,
        t
      )
      if (lowerBoundError !== true) {
        errors.lowerBound = {type: 'validate', message: lowerBoundError}
      }

      const upperBoundError = validateUpperBoundValue(
        values.upperBound,
        values.target,
        values.lowerBound,
        t
      )
      if (upperBoundError !== true) {
        errors.upperBound = {type: 'validate', message: upperBoundError}
      }

      const validFromError = validateValidFromValue(
        values.validFrom,
        isEdit,
        values.dayPeriod,
        existingTargets,
        backendTargets,
        timezone,
        localDateToISOInTimezone,
        t
      )
      if (validFromError !== true) {
        errors.validFrom = {type: 'validate', message: validFromError}
      }

      return {
        values: Object.keys(errors).length === 0 ? values : {},
        errors
      }
    }
  })

  const {
    watch,
    reset,
    setError,
    formState: {errors}
  } = form

  const targetValue = watch('target')
  const lowerBoundValue = watch('lowerBound')
  const upperBoundValue = watch('upperBound')

  const showAtLeastOneValueError = !hasAtLeastOneValue(
    targetValue,
    lowerBoundValue,
    upperBoundValue
  )

  const resetFormForCreateAnother = useCallback(
    (data: StrengthTargetFormData, newTarget: StrengthTarget) => {
      const updatedTargets = [...existingTargets, newTarget]

      const nextDayISO = moment
        .utc(data.validFrom)
        .tz(timezone)
        .add(1, 'day')
        .startOf('day')
        .toISOString()

      const nextAvailableDate = findNextAvailableDate(
        data.dayPeriod,
        updatedTargets,
        backendTargets,
        nextDayISO,
        isoToLocalDateInTimezone,
        timezone
      )

      reset({
        dayPeriod: data.dayPeriod,
        target: undefined,
        lowerBound: undefined,
        upperBound: undefined,
        validFrom: nextAvailableDate,
        comment: '',
        createAnother: true
      })

      setCreateAnother(true)
    },
    [existingTargets, backendTargets, timezone, reset]
  )

  const onSubmit = useCallback(
    (data: StrengthTargetFormData) => {
      if (!hasAtLeastOneValue(data.target, data.lowerBound, data.upperBound)) {
        return
      }

      if (!isEdit) {
        const validFromISO = localDateToISOInTimezone(data.validFrom, timezone)
        const duplicate = existingTargets.find(
          (existingTarget) =>
            existingTarget.dayPeriod === data.dayPeriod &&
            compareDateStrings(existingTarget.validFrom, validFromISO) === 0
        )

        if (duplicate) {
          setError('validFrom', {
            type: 'manual',
            message: t('materialsSettings.strengthTargets.validation.targetAlreadyExistsForDate')
          })
          return
        }
      }

      const newTarget: StrengthTarget = {
        id: target?.id || `${Date.now()}`,
        dayPeriod: data.dayPeriod,
        target: data.target,
        lowerBound: data.lowerBound,
        upperBound: data.upperBound,
        validFrom: localDateToISOInTimezone(data.validFrom, timezone),
        comment: data.comment,
        isCurrent: false
      }

      onSave(newTarget)

      if (data.createAnother) {
        resetFormForCreateAnother(data, newTarget)
      } else {
        onClose()
      }
    },
    [
      target,
      timezone,
      onSave,
      onClose,
      isEdit,
      existingTargets,
      setError,
      t,
      resetFormForCreateAnother
    ]
  )

  const hasAnyErrors = Object.keys(errors).length > 0 || showAtLeastOneValueError

  return {
    form,
    isEdit,
    createAnother,
    showAtLeastOneValueError,
    hasAnyErrors,
    setCreateAnother,
    onSubmit,
    resetFormForCreateAnother
  }
}
